import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, Loader2, Music, Play, Pause, X, Check, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MusicSelector({ onSelect, onClose, currentMusic }) {
  const { token } = useAuth();
  const [query, setQuery] = useState('');
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(currentMusic || null);
  const audioRef = useRef(null);
  const debounceRef = useRef(null);

  const searchMusic = useCallback(async (value) => {
    if (value.length < 2) {
      setTracks([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/music/search?q=${encodeURIComponent(value)}&limit=20`);
        setTracks(response.data.tracks || []);
      } catch (error) {
        console.error('Music search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  const handlePlay = (track) => {
    if (playing === track.id) {
      // Pause
      audioRef.current?.pause();
      setPlaying(null);
    } else {
      // Play new track
      if (audioRef.current) {
        audioRef.current.src = track.preview_url;
        audioRef.current.play().catch(console.error);
        setPlaying(track.id);
      }
    }
  };

  const handleSelect = (track) => {
    setSelectedTrack(track);
  };

  const handleConfirm = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    onSelect(selectedTrack);
  };

  const handleClear = () => {
    setSelectedTrack(null);
    onSelect(null);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Handle audio ended
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => setPlaying(null);
      audio.addEventListener('ended', handleEnded);
      return () => audio.removeEventListener('ended', handleEnded);
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black/95 z-[200] flex flex-col">
      {/* Hidden audio element */}
      <audio ref={audioRef} />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border/30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-6 h-6" />
          </Button>
          <div className="flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-lg">Add Music</h2>
          </div>
        </div>
        
        <Button 
          onClick={handleConfirm} 
          disabled={!selectedTrack}
          className="rounded-full"
        >
          <Check className="w-4 h-4 mr-2" />
          Done
        </Button>
      </div>

      {/* Selected Track Preview */}
      {selectedTrack && (
        <div className="p-4 bg-primary/10 border-b border-border/30">
          <div className="flex items-center gap-3">
            <img 
              src={selectedTrack.cover} 
              alt={selectedTrack.title}
              className="w-14 h-14 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{selectedTrack.title}</p>
              <p className="text-sm text-muted-foreground truncate">{selectedTrack.artist}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClear}
              className="text-destructive"
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search for songs, artists..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              searchMusic(e.target.value);
            }}
            className="pl-12 h-12 rounded-full bg-secondary/30"
            autoFocus
          />
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-4 pb-20">
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {!loading && query.length >= 2 && tracks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Music className="w-16 h-16 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">No music found</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Try a different search</p>
          </div>
        )}

        {!loading && tracks.length === 0 && query.length < 2 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Music className="w-16 h-16 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">Search for music</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Find songs to add to your video</p>
          </div>
        )}

        <div className="space-y-2">
          {tracks.map((track) => (
            <div
              key={track.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-xl transition-colors cursor-pointer",
                selectedTrack?.id === track.id 
                  ? "bg-primary/20 ring-1 ring-primary" 
                  : "hover:bg-secondary/30"
              )}
              onClick={() => handleSelect(track)}
            >
              {/* Cover */}
              <div className="relative">
                <img 
                  src={track.cover} 
                  alt={track.title}
                  className="w-14 h-14 rounded-lg object-cover"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlay(track);
                  }}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg opacity-0 hover:opacity-100 transition-opacity"
                >
                  {playing === track.id ? (
                    <Pause className="w-6 h-6 text-white" fill="white" />
                  ) : (
                    <Play className="w-6 h-6 text-white" fill="white" />
                  )}
                </button>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{track.title}</p>
                <p className="text-sm text-muted-foreground truncate">{track.artist}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground/70">
                  <Clock className="w-3 h-3" />
                  <span>30s preview</span>
                </div>
              </div>

              {/* Selection indicator */}
              {selectedTrack?.id === track.id && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-5 h-5 text-primary-foreground" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
        <p className="text-center text-xs text-muted-foreground">
          Music provided by Deezer • 30 second previews
        </p>
      </div>
    </div>
  );
}
